import { BitkitPageSidebar, IconChevronLeft, rem, useResponsive } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Separator } from '@chakra-ui/react/separator';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { ComponentType, ReactNode, RefObject, useContext, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';

import DynamicBanners from '@/components/banners/DynamicBanners/DynamicBanners';
import WelcomeBanner from '@/components/banners/WelcomeBanner/WelcomeBanner';
import InternalFooter from '@/components/InternalFooter/InternalFooter';
import OnboardingChecklistMount from '@/components/OnboardingChecklist/OnboardingChecklistMount';
import { SidebarInsetContext } from '@/components/page/ContentWrapper';
import Page, { PageProps } from '@/components/page/Page';
import PlanDowngradeBanner from '@/components/PlanDowngrade/PlanDowngradeBanner/PlanDowngradeBanner';
import PlanDowngradeDialog from '@/components/PlanDowngrade/PlanDowngradeDialog/PlanDowngradeDialog';
import TimeBasedTrialBanner from '@/components/TimeBasedTrial/TimeBasedTrialBanner/TimeBasedTrialBanner';
import TrialUpdateDialog from '@/components/TimeBasedTrial/TrialUpdateDialog/TrialUpdateDialog';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';
import useFlashToast from '@/hooks/useFlashToast';
import { isCiProductArea } from '@/utils/productArea';

import { GlobalPropsContext } from './GlobalPropsContext';
import PlatformShell from './PlatformShell/PlatformShell';

/**
 * Opens the collapsed sidebar. The design system has no notion of an opener -- `PageSidebar.Title`'s
 * back button is a *back* affordance and only its chevron is clickable, while this row is the
 * target in full, as it has always been here.
 */
const SidebarOpenButton = ({
  buttonRef,
  onClick,
  title,
}: {
  buttonRef?: RefObject<HTMLButtonElement | null>;
  onClick: () => void;
  title: string;
}) => (
  <chakra.button
    ref={buttonRef}
    alignItems="center"
    display="flex"
    gap="4"
    // `rem`, not `"56"`: 56 is not on the size scale, so the bare string produced no rule at all and
    // the row collapsed to its 24px content.
    height={rem(56)}
    onClick={onClick}
    paddingInlineStart="16"
    type="button"
    width="100%"
  >
    <IconChevronLeft color="icon/primary" />
    <Text as="span" color="text/primary" textStyle="body/lg/regular">
      {title}
    </Text>
  </chakra.button>
);

export interface ProtectedPageProps extends PageProps {
  bannerComponent?: ReactNode;
  sidebarCollapse?: 'mobile' | 'tablet';
  colorScheme?: 'white' | 'grey';
  helpDrawerContent?: ReactNode;
  footerBackground?: string;
  sidebarContent?: ReactNode;
  /** The row that opens the collapsed sidebar, for a page that needs to point at it (a tour). */
  sidebarOpenButtonRef?: RefObject<HTMLButtonElement | null>;
  sidebarTitle?: string;
  sidebarWidth?: string;
  showLimitedTrialBanner?: boolean;
  withoutFooter?: boolean;
}

// The account-level banner cascade shown when a page does not provide its own BannerComponent.
// Exported so a custom BannerComponent that has nothing to show can fall back to it instead of
// suppressing these banners (a custom banner element is always truthy, so ProtectedPage itself
// cannot tell whether it rendered anything).
export const DefaultBanners = ({ showLimitedTrialBanner = false }: { showLimitedTrialBanner?: boolean }) => {
  const { account } = useContext(GlobalPropsContext);

  if (window.location.pathname.includes('/build-cache/')) {
    return null;
  }

  const canActivateProTrial = (account?.isRestricted || account?.isHobby) && account?.canStartProTrial;
  if (canActivateProTrial && showLimitedTrialBanner) {
    return <WelcomeBanner />;
  }

  if (account?.onTimeBasedTrial) {
    return <TimeBasedTrialBanner />;
  }

  if (account?.downgradeNotifications?.banner) {
    return <PlanDowngradeBanner />;
  }

  return <DynamicBanners />;
};

const ProtectedPage = ({
  bannerComponent,
  bg,
  children,
  colorScheme = 'white',
  footerBackground,
  helpDrawerContent,
  sidebarContent,
  sidebarOpenButtonRef,
  sidebarTitle = '',
  sidebarWidth = '16rem',
  showLimitedTrialBanner,
  withoutFooter,
  sidebarCollapse = 'mobile',
  ...rest
}: ProtectedPageProps) => {
  const { flash, account } = useContext(GlobalPropsContext);
  useFlashToast(flash);

  const { isTablet, isMobile } = useResponsive();
  const isSidebarCollapsed = sidebarCollapse === 'tablet' ? isTablet || isMobile : isMobile;

  const { open: isSidebarOpen, onOpen: openSidebar } = useDisclosure();
  const footerBg = colorScheme === 'grey' ? 'background/primary' : footerBackground;
  const pageBg = colorScheme === 'grey' ? 'background/secondary' : bg;

  const banner = bannerComponent || <DefaultBanners showLimitedTrialBanner={showLimitedTrialBanner} />;

  const bannerRef = useRef<HTMLDivElement>(null);
  const { height = 0 } = useResizeObserver({ ref: bannerRef });
  const aboveContentHeight = height;
  const bannerBox = banner && <Box paddingLeft={['unset', NAV_WIDTH]}>{banner}</Box>;

  let mainMarginLeft: BoxProps['marginLeft'];
  if (sidebarContent) {
    if (sidebarCollapse === 'tablet') {
      mainMarginLeft = ['unset', NAV_WIDTH, sidebarWidth];
    } else {
      mainMarginLeft = ['unset', sidebarWidth];
    }
  }
  return (
    <Page
      {...rest}
      bg={pageBg}
      paddingTop={HEADER_HEIGHT + height}
      paddingLeft={!isSidebarCollapsed ? NAV_WIDTH : undefined}
    >
      <Box
        background="background/primary"
        width={`calc(100% - ${NAV_WIDTH}px)`}
        height={HEADER_HEIGHT + height}
        position="absolute"
        top="0"
      />
      <PlatformShell helpDrawerContent={helpDrawerContent}>
        <div ref={bannerRef}>{bannerBox}</div>
      </PlatformShell>

      {sidebarContent && isSidebarCollapsed && !isSidebarOpen && (
        <Box
          paddingLeft={sidebarCollapse === 'tablet' ? [0, NAV_WIDTH, 0] : undefined}
          backgroundColor="background/primary"
          // A scroll lock (tour, dialog) pads the page by the scrollbar it hides and sets this variable;
          // without it the row stops short of the edge while the header above still spans it.
          marginInlineEnd="calc(var(--scrollbar-width, 0px) * -1)"
        >
          <SidebarOpenButton buttonRef={sidebarOpenButtonRef} onClick={openSidebar} title={sidebarTitle} />
          <Separator />
        </Box>
      )}

      {sidebarContent && (!isSidebarCollapsed || isSidebarOpen) && (
        <BitkitPageSidebar
          height={`calc(100vh - ${HEADER_HEIGHT + aboveContentHeight}px)`}
          insetBlockStart={`${HEADER_HEIGHT + aboveContentHeight}px`}
          insetInlineStart={!isMobile ? NAV_WIDTH : '0'}
          // The shell sizes itself to the viewport below the platform header, so the component's own
          // `100dvh` floor has to give way -- see the BitkitPageSidebar migration notes.
          minHeight="auto"
          overflowY="auto"
          position="fixed"
          width={sidebarCollapse === 'tablet' ? ['100%', '100%', sidebarWidth] : ['100%', sidebarWidth]}
          // The recipe caps the sidebar at 320 from `tablet` up, which is right for a column beside
          // a page and wrong for this one: the `tablet` caller opens it as the whole view between
          // the two breakpoints -- `main` is unmounted while it is open -- so the 100% above would
          // come out as a 320 panel next to a blank page. Follows `width` at every stop.
          maxWidth={sidebarCollapse === 'tablet' ? ['none', 'none', sidebarWidth] : undefined}
        >
          {sidebarTitle && <BitkitPageSidebar.Title>{sidebarTitle}</BitkitPageSidebar.Title>}
          {/* No `Body` here: every caller composes its own `Body` (and mostly `Footer`) inside
              `sidebarContent`, so wrapping again would nest the slot and apply its layout twice. */}
          {sidebarContent}
        </BitkitPageSidebar>
      )}

      {(!isSidebarCollapsed || !isSidebarOpen) && (
        <Box as="main" flexGrow={1} marginLeft={mainMarginLeft} minHeight="100%" display="flex" flexDirection="column">
          <SidebarInsetContext.Provider value={sidebarContent && !isSidebarCollapsed ? sidebarWidth : undefined}>
            {children}
          </SidebarInsetContext.Provider>
        </Box>
      )}

      {!withoutFooter && (
        <InternalFooter
          bg={footerBg}
          marginTop="auto"
          paddingLeft={
            sidebarCollapse === 'tablet'
              ? ['12', '12', sidebarContent ? `calc(${sidebarWidth} + 12px)` : '12']
              : ['12', sidebarContent ? `calc(${sidebarWidth} + 12px)` : '12']
          }
        />
      )}
      <TrialUpdateDialog />
      {account && <PlanDowngradeDialog account={account} />}
      {isCiProductArea() && <OnboardingChecklistMount />}
    </Page>
  );
};

export const withProtectedPage = <P extends object>(
  WrappedComponent: ComponentType<P>,
  pageProps?: ProtectedPageProps,
): ComponentType<P> => {
  const WithProtectedPage = (props: P) => (
    <ProtectedPage {...pageProps}>
      <WrappedComponent {...props} />
    </ProtectedPage>
  );

  WithProtectedPage.displayName = `withProtectedPage(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithProtectedPage;
};

export default ProtectedPage;
